import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '../utils/api';

export type RealtimeKind = 'hotspot' | 'lightning' | 'hazard' | 'ignition' | 'burnt_area';

export interface RealtimeKindsSetting {
  /** Whether this user may change the setting. Only FiSci staff can; a tenant
   *  admin sees the state read-only. Decided by the server — never inferred
   *  from user flags on the client. */
  can_edit: boolean;
  /** What this tenant has switched on. */
  realtime_kinds: Record<RealtimeKind, boolean>;
  /** Whether a data source exists at all — a kind with none is greyed out,
   *  since enabling it could never produce data. */
  configured_kinds: Record<RealtimeKind, boolean>;
}

interface DataEnvelope<T> {
  data: T;
}

const KEY = ['tenant-realtime-kinds'];
const URL = '/api/admin/tenant/realtime-kinds';

export const useRealtimeKindsSetting = () =>
  useQuery({
    queryKey: KEY,
    queryFn: async () => {
      const res = await api.request<DataEnvelope<RealtimeKindsSetting>>(URL);
      return res.data;
    },
  });

/**
 * Same setting, for surfaces that non-staff users can reach (the Data
 * Management console). A 403 — plain user — resolves to null instead of an
 * error state, meaning "cannot manage realtime sources at all".
 */
export const useRealtimeKindsSettingIfAllowed = () =>
  useQuery({
    queryKey: [...KEY, 'if-allowed'],
    queryFn: async (): Promise<RealtimeKindsSetting | null> => {
      try {
        const res = await api.request<DataEnvelope<RealtimeKindsSetting>>(URL);
        return res.data;
      } catch (err) {
        if ((err as { status?: number })?.status === 403) return null;
        throw err;
      }
    },
    retry: false,
  });

export const useUpdateRealtimeKinds = () => {
  const queryClient = useQueryClient();
  return useMutation({
    // Sends only the toggled kind — the backend merges per key, so flipping
    // one never clears the other.
    mutationFn: (update: Partial<Record<RealtimeKind, boolean>>) =>
      api.request<DataEnvelope<RealtimeKindsSetting>>(URL, {
        method: 'PATCH',
        body: { realtime_kinds: update },
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: KEY });
      // Project settings read availability from the project payload, which
      // now depends on this.
      queryClient.invalidateQueries({ queryKey: ['project'] });
    },
  });
};
